import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from '../hooks/use-toast';

/**
 * Contact Form Component with Formspree Integration
 * 
 * SETUP INSTRUCTIONS:
 * 1. Go to https://formspree.io and create a free account
 * 2. Create a new form and get your form endpoint (format: https://formspree.io/f/YOUR_FORM_ID)
 * 3. Replace 'YOUR_FORM_ID' below with your actual Formspree form ID
 * 4. Configure email notifications in your Formspree dashboard
 * 
 * All form submissions will be sent directly to your email via Formspree.
 * No backend or database required - perfect for static hosting on Hostinger.
 */

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    service: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // FORMSPREE CONFIGURATION
  // Replace 'YOUR_FORM_ID' with your actual Formspree form ID
  const FORMSPREE_ENDPOINT = 'https://formspree.io/f/YOUR_FORM_ID';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Submit to Formspree
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast({
          title: "Message Sent Successfully",
          description: "We'll get back to you within 24 hours.",
        });

        // Reset form
        setFormData({
          name: '',
          email: '',
          company: '',
          service: '',
          message: ''
        });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again or email us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="contact-form">
      <div className="form-grid">
        <div className="form-group">
          <label htmlFor="name" className="form-label">Your Name</label>
          <Input
            id="name"
            name="name"
            type="text"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="John Doe"
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label htmlFor="email" className="form-label">Email Address</label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
            placeholder="john@company.com"
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label htmlFor="company" className="form-label">Company</label>
          <Input
            id="company"
            name="company"
            type="text"
            value={formData.company}
            onChange={handleChange}
            placeholder="Your Company"
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label htmlFor="service" className="form-label">Service Interest</label>
          <select
            id="service"
            name="service"
            value={formData.service}
            onChange={handleChange}
            required
            className="form-select"
          >
            <option value="">Select a service</option>
            <option value="solo">The Solo</option>
            <option value="studio">The Studio</option>
            <option value="custom">The Custom</option>
            <option value="consultation">Initial Consultation</option>
          </select>
        </div>
      </div>

      <div className="form-group form-group-full">
        <label htmlFor="message" className="form-label">Project Details</label>
        <Textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          placeholder="Tell us about your vision and business objectives..."
          className="form-textarea"
          rows={6}
        />
      </div>

      <Button
        type="submit"
        disabled={isSubmitting}
        className="form-submit-btn"
      >
        {isSubmitting ? 'Sending...' : 'Initiate Project'}
      </Button>
    </form>
  );
};

export default ContactForm;
